/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useEffect, useState } from 'react'

import { EuiSkeletonText, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { CuiAlert } from '@modules/cui/Alert'
import { startPageActions } from '@modules/utils/apm'
import type { SaasUserDomain } from '@modules/ui-types'
import withBillingNotificationsCenter from '@modules/billing-components/BillingNotificationsCenter/withBillingNotificationsCenter'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { isMarketplaceCustomer } from '@modules/billing-lib/billingDetails/utils'

import BillingStatements from './BillingStatements'
import BillingInvoices from './BillingInvoices'
import BillingHistoryPanel from './BillingHistoryPanel'
import BillingHistoryEmptyPrompt from './BillingHistoryEmptyPrompt'
import MarketplaceUserContent from './MarketplaceUserContent'
import useBillingHistory from './useBillingHistory'

import type { FunctionComponent } from 'react'

// This component manages its own page transactions because it interweaves
// AWS and non-AWS behaviour, making it unsuitable to use with `withLoading`.
// Probably this should be two separate components.
const BillingHistory: FunctionComponent = () => {
  const [startedTransaction, setStartedTransaction] = useState(false)
  const { billingHistory, isLoading, error } = useBillingHistory()
  const { data: billingData } = useBillingDetails()

  useEffect(() => {
    if (!startedTransaction) {
      setStartedTransaction(true)
      startPageActions(`Billing history`)
    }
  }, [startedTransaction])

  if (error) {
    return (
      <BillingHistoryPanel>
        <CuiAlert type='error'>{error}</CuiAlert>
      </BillingHistoryPanel>
    )
  }

  if (typeof billingHistory === 'undefined') {
    return <EuiSkeletonText data-test-id='billing-history.loading-skeleton' />
  }

  const { statements, invoices } = billingHistory
  const hasStatements = statements.length > 0
  const hasInvoices = invoices.length > 0

  return (
    <Fragment>
      {billingData && isMarketplaceCustomer(billingData) && (
        <Fragment>
          <MarketplaceUserContent
            domain={billingData.channel as SaasUserDomain}
            hideTitle={hasStatements || hasInvoices}
          />
          <EuiSpacer />
        </Fragment>
      )}

      {!hasStatements && !hasInvoices && <BillingHistoryEmptyPrompt />}

      {hasStatements && (
        <Fragment>
          <BillingStatements
            loading={isLoading}
            statements={statements}
            title={
              hasInvoices ? (
                <FormattedMessage
                  id='billing-statements.statements.heading'
                  defaultMessage='Paid by balance'
                />
              ) : undefined
            }
          />
          <EuiSpacer />
        </Fragment>
      )}

      {hasInvoices && (
        <BillingInvoices
          invoices={invoices}
          loading={isLoading}
          title={
            hasStatements ? (
              <FormattedMessage
                id='billing-statements.statements.heading'
                defaultMessage='Paid by balance'
              />
            ) : undefined
          }
        />
      )}
    </Fragment>
  )
}

export default withBillingNotificationsCenter(BillingHistory)
